import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Badge,
  Typography,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Popper,
  Grow,
  ClickAwayListener
} from '@mui/material';
import logoDark from "../assets/logo-dark.png";
import imachLogo360 from "../assets/login//iMach_360_logo.png";
import imachLogo360Dark from "../assets/imach360-Alt dark.png";
import imachLogo from "../assets/login/logo.png";
import {
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AccountCircle as ProfileIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  FiberManualRecord as UnreadIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Paid as ExpenseIcon,
  CalendarToday as CalendarIcon,
  Inbox as InboxIcon,
  Support as TicketIcon,
  Business as AssetIcon,
  MenuBook as FormIcon,
  HolidayVillage as HolidayIcon,
  Folder as ResourceIcon
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { notificationAPI } from '../services/api';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  date: string;
  path: string;
}

interface SearchResult {
  id: string;
  title: string;
  description: string;
  path: string;
  category: string;
  icon: React.ReactNode;
  keywords: string[];
}

// Global search configuration
const searchableItems: SearchResult[] = [
  // Dashboard
  {
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Main dashboard with overview',
    path: '/dashboard',
    category: 'Main',
    icon: <DashboardIcon />,
    keywords: ['dashboard', 'home', 'overview', 'main']
  },
  // Leave Management
  {
    id: 'leave-dashboard',
    title: 'Leave Dashboard',
    description: 'Leave management overview',
    path: '/leaves/dashboard',
    category: 'Leave Management',
    icon: <CalendarIcon />,
    keywords: ['leave', 'dashboard', 'absence', 'time off']
  },
  {
    id: 'leave-apply',
    title: 'Apply Leave',
    description: 'Submit a new leave request',
    path: '/leaves/apply',
    category: 'Leave Management',
    icon: <AssignmentIcon />,
    keywords: ['leave', 'apply', 'request', 'absence', 'vacation']
  },
  {
    id: 'my-leaves',
    title: 'My Leaves',
    description: 'View my leave history',
    path: '/leaves/my-leaves',
    category: 'Leave Management',
    icon: <CalendarIcon />,
    keywords: ['leave', 'my', 'history', 'requests']
  },
  // Asset Management
  {
    id: 'view-assets',
    title: 'View Assets',
    description: 'Browse all company assets',
    path: '/assets/view',
    category: 'Asset Management',
    icon: <AssetIcon />,
    keywords: ['asset', 'view', 'inventory', 'equipment']
  },
  {
    id: 'my-assets',
    title: 'My Assets',
    description: 'Assets assigned to me',
    path: '/assets/my-assets',
    category: 'Asset Management',
    icon: <AssetIcon />,
    keywords: ['asset', 'my', 'assigned', 'personal']
  },
  {
    id: 'add-asset',
    title: 'Add Asset',
    description: 'Add a new asset to inventory',
    path: '/assets/add',
    category: 'Asset Management',
    icon: <AssetIcon />,
    keywords: ['asset', 'add', 'new', 'create', 'inventory']
  },
  // HR Management
  {
    id: 'employees',
    title: 'Employee List',
    description: 'View all employees',
    path: '/hr/employees',
    category: 'HR Management',
    icon: <PeopleIcon />,
    keywords: ['employee', 'staff', 'team', 'personnel', 'hr']
  },
  // Expense Management
  {
    id: 'expense-dashboard',
    title: 'Expense Dashboard',
    description: 'Expense claims overview',
    path: '/expenses/dashboard',
    category: 'Expense Management',
    icon: <ExpenseIcon />,
    keywords: ['expense', 'dashboard', 'claims', 'reimbursement']
  },
  {
    id: 'submit-claim',
    title: 'Submit Expense Claim',
    description: 'Submit a new expense claim',
    path: '/expenses/submit',
    category: 'Expense Management',
    icon: <ExpenseIcon />,
    keywords: ['expense', 'claim', 'submit', 'reimbursement', 'money']
  },
  {
    id: 'my-claims',
    title: 'My Expense Claims',
    description: 'View my expense claims',
    path: '/expenses/my-claims',
    category: 'Expense Management',
    icon: <ExpenseIcon />,
    keywords: ['expense', 'claim', 'my', 'history', 'reimbursement']
  },
  // Form Management
  {
    id: 'available-forms',
    title: 'Available Forms',
    description: 'Browse available forms',
    path: '/forms/available',
    category: 'Form Management',
    icon: <FormIcon />,
    keywords: ['form', 'available', 'template', 'document']
  },
  {
    id: 'my-forms',
    title: 'My Forms',
    description: 'Forms I created or filled',
    path: '/forms/my-forms',
    category: 'Form Management',
    icon: <FormIcon />,
    keywords: ['form', 'my', 'created', 'filled']
  },
  {
    id: 'create-form',
    title: 'Create Form',
    description: 'Create a new form',
    path: '/forms/create',
    category: 'Form Management',
    icon: <FormIcon />,
    keywords: ['form', 'create', 'new', 'build']
  },
  // Holiday Management
  {
    id: 'holiday-calendar',
    title: 'Holiday Calendar',
    description: 'View company holidays',
    path: '/holidays/calendar',
    category: 'Holiday Management',
    icon: <HolidayIcon />,
    keywords: ['holiday', 'calendar', 'vacation', 'days off']
  },
  // Resource Directory
  {
    id: 'resource-directory',
    title: 'Resource Directory',
    description: 'Browse company resources',
    path: '/resources/directory',
    category: 'Resources',
    icon: <ResourceIcon />,
    keywords: ['resource', 'directory', 'documents', 'files']
  },
  // Other
  {
    id: 'inbox',
    title: 'Inbox',
    description: 'View pending approvals and requests',
    path: '/inbox',
    category: 'Workflow',
    icon: <InboxIcon />,
    keywords: ['inbox', 'approvals', 'pending', 'requests', 'tasks']
  },
  {
    id: 'raise-ticket',
    title: 'Raise Ticket',
    description: 'Submit a support ticket',
    path: '/tickets/raise',
    category: 'Support',
    icon: <TicketIcon />,
    keywords: ['ticket', 'support', 'help', 'issue', 'problem']
  },
  {
    id: 'approve-tickets',
    title: 'Ticket Approvals',
    description: 'View and manage support tickets',
    path: '/tickets/approve',
    category: 'Support',
    icon: <TicketIcon />,
    keywords: ['ticket', 'approval', 'manage', 'support', 'resolve', 'close']
  },
  {
    id: 'profile',
    title: 'My Profile',
    description: 'View and edit my profile',
    path: '/profile',
    category: 'Account',
    icon: <ProfileIcon />,
    keywords: ['profile', 'account', 'personal', 'settings']
  },
  {
    id: 'settings',
    title: 'Settings',
    description: 'Application settings',
    path: '/settings',
    category: 'Account',
    icon: <SettingsIcon />,
    keywords: ['settings', 'preferences', 'configuration']
  }
];

interface ResponsiveNavbarProps {
}

const ResponsiveNavbar: React.FC<ResponsiveNavbarProps> = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Search related states
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchAnchorEl, setSearchAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchNotificationCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await notificationAPI.getPendingCount();
      setNotificationCount(response.data.count);
    } catch (err) {
      console.error('Failed to fetch notification count:', err);
    }
  }, [isAuthenticated]);

  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const response = await notificationAPI.getPending();
      setNotifications(response.data);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    fetchNotificationCount();
    // Refresh count every 60 seconds
    const interval = setInterval(fetchNotificationCount, 60000);
    return () => clearInterval(interval);
  }, [fetchNotificationCount]);

  const handleProfileOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchor(null);
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
    fetchNotifications();
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleNotificationClick = (path: string) => {
    navigate(path);
    handleNotificationClose();
  };

  const handleLogout = async () => {
    handleProfileClose();
    setLogoutDialogOpen(true);
  };

  const confirmLogout = async () => {
    setLogoutDialogOpen(false);
    await logout();
    // Use window.location.replace for cleaner redirect without history
    window.location.replace('/login');
  };

  const cancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const handleSettings = () => {
    navigate('/settings');
    handleProfileClose();
  };



  const performSearch = useCallback((query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    const normalizedQuery = query.toLowerCase().trim();
    
    const filtered = searchableItems.filter(item => {
      const matchesTitle = item.title.toLowerCase().includes(normalizedQuery);
      const matchesDescription = item.description.toLowerCase().includes(normalizedQuery);
      const matchesKeywords = item.keywords.some(keyword => 
        keyword.toLowerCase().includes(normalizedQuery)
      );
      const matchesCategory = item.category.toLowerCase().includes(normalizedQuery);
      
      return matchesTitle || matchesDescription || matchesKeywords || matchesCategory;
    }).slice(0, 8); // Limit to 8 results

    setSearchResults(filtered);
    setSearchLoading(false);
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    performSearch(query);
  };

  const handleSearchFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setSearchAnchorEl(event.currentTarget);
    if (searchQuery.trim()) {
      performSearch(searchQuery);
    }
  };

  const handleSearchClick = (result: SearchResult) => {
    navigate(result.path);
    setSearchQuery('');
    setSearchResults([]);
    setSearchAnchorEl(null);
  };

  const handleSearchClose = () => {
    setSearchAnchorEl(null);
  };

  const handleMobileSearchOpen = () => {
    setMobileSearchOpen(true);
  };

  const handleMobileSearchClose = () => {
    setMobileSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSearch = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      handleSearchClick(searchResults[0]);
    }
  };

  const themeColors = {
    surface: isDarkMode ? '#1e293b' : '#f8fafc',
    text: isDarkMode ? '#f1f5f9' : '#1e293b',
    textSecondary: isDarkMode ? '#cbd5e1' : '#64748b',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    accent: '#9358f7',
    accentLight: '#a066f9',
    accentHover: isDarkMode ? 'rgba(147, 88, 247, 0.15)' : 'rgba(147, 88, 247, 0.1)',
    primary: isDarkMode ? '#9358f7' : '#9358f7',
    primaryLight: isDarkMode ? '#a066f9' : '#a066f9',
    error: '#ef4444'
  };

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          zIndex: 1000,
          backgroundColor: themeColors.surface,
          color: themeColors.text,
          boxShadow: `0 1px 3px ${isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)'}`,
          borderBottom: `1px solid ${themeColors.border}`
        }}
      >
        <Toolbar
          sx={{
            gap: { xs: 1, sm: 2 },
            px: { xs: 1, sm: 2 },
            py: { xs: 1, sm: 0 },
            minHeight: { xs: 56, sm: 64 },
            maxHeight: { xs: 56, sm: 64 },
            height: { xs: 56, sm: 64 },
            overflow: 'visible',
            display: 'flex',
            alignItems: 'center',
            position: 'relative'
          }}
        >
          {/* Logo - Left (desktop only) */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 1,
              flexShrink: 0
            }}
          >
            <img
              src={isDarkMode ? logoDark : imachLogo}
              alt="iMach 360 Logo"
              style={{ height: 30, objectFit: 'contain' }}
            />
          </Box>

          {/* Logo - Mobile left */}
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              alignItems: 'center',
              gap: 1,
              flexShrink: 0
            }}
          >
            <img
              src={isDarkMode ? imachLogo360Dark : imachLogo360}
              alt="iMach 360 Logo"
              style={{ height: 40, objectFit: 'contain' }}
            />
          </Box>

          {/* Center Title - Absolutely centered on desktop */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              justifyContent: 'center'
            }}
          >
            <img
              src={isDarkMode ? imachLogo360Dark : imachLogo360}
              alt="iMach 360 Logo"
              style={{ height: 60, objectFit: 'contain' }}
            />

          </Box>

          {/* Search - Hidden on mobile, visible on md and up */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexShrink: 0,
              maxWidth: '400px'
            }}
          >
            <TextField
              size="small"
              placeholder="Search..."
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onKeyPress={handleSearch}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDarkMode ? 'rgba(15, 118, 110, 0.08)' : 'rgba(15, 118, 110, 0.06)',
                  transition: 'all 150ms ease-in-out',
                  '& fieldset': {
                    borderColor: themeColors.border
                  },
                  '&:hover fieldset': {
                    borderColor: themeColors.accent
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: themeColors.accent,
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputBase-input': {
                  color: themeColors.text,
                  fontSize: '14px',
                  '&::placeholder': {
                    color: themeColors.textSecondary,
                    opacity: 1
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: themeColors.accent, fontSize: '20px' }} />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {/* Right side actions */}
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center', marginLeft: 'auto' }}>
            {/* Search icon - Mobile only */}
            <IconButton
              onClick={handleMobileSearchOpen}
              size="small"
              sx={{
                display: { xs: 'flex', sm: 'none' },
                color: themeColors.textSecondary,
                '&:hover': {
                  color: themeColors.accent,
                  backgroundColor: themeColors.accentHover
                }
              }}
            >
              <SearchIcon sx={{ fontSize: '20px' }} />
            </IconButton>

            {/* Notifications */}
            <IconButton
              onClick={handleNotificationOpen}
              size="small"
              sx={{
                color: themeColors.textSecondary,
                transition: 'all 150ms ease-in-out',
                '&:hover': {
                  color: themeColors.accent,
                  backgroundColor: isDarkMode ? 'rgba(15, 118, 110, 0.12)' : 'rgba(15, 118, 110, 0.08)'
                }
              }}
              title="Notifications"
            >
              <Badge badgeContent={notificationCount} color="error">
                <NotificationsIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
              </Badge>
            </IconButton>

            {/* Dark mode toggle */}
            <IconButton
              onClick={toggleDarkMode}
              size="small"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={{
                color: themeColors.textSecondary,
                transition: 'all 150ms ease-in-out',
                '&:hover': {
                  color: themeColors.accent,
                  backgroundColor: isDarkMode ? 'rgba(15, 118, 110, 0.12)' : 'rgba(15, 118, 110, 0.08)'
                }
              }}
            >
              {isDarkMode ? <LightModeIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} /> : <DarkModeIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />}
            </IconButton>



            {/* Settings - Desktop only */}
            <IconButton
              onClick={() => navigate('/settings')}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                color: themeColors.textSecondary,
                transition: 'all 150ms ease-in-out',
                '&:hover': {
                  color: themeColors.accent,
                  backgroundColor: isDarkMode ? 'rgba(15, 118, 110, 0.12)' : 'rgba(15, 118, 110, 0.08)'
                }
              }}
              title="Settings"
            >
              <SettingsIcon />
            </IconButton>

            {/* Profile Avatar */}
            <IconButton
              onClick={handleProfileOpen}
              size="small"
              sx={{
                transition: 'all 150ms ease-in-out',
                padding: { xs: '4px', sm: '8px' },
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            >
              <Avatar
                src={user?.profilePicture}
                alt={user?.name}
                sx={{
                  width: { xs: 32, sm: 36 },
                  height: { xs: 32, sm: 36 },
                  cursor: 'pointer',
                  border: `2px solid ${themeColors.accent}`,
                  transition: 'all 150ms ease-in-out',
                  '&:hover': {
                    boxShadow: `0 4px 12px ${isDarkMode ? 'rgba(15, 118, 110, 0.3)' : 'rgba(15, 118, 110, 0.2)'}`
                  }
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationClose}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: themeColors.surface,
              color: themeColors.text,
              borderColor: themeColors.border,
              border: `1px solid ${themeColors.border}`,
              width: { xs: '90vw', sm: '320px' },
              maxHeight: '400px',
              mt: 1.5,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: '12px'
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700}>Notifications</Typography>
          {notificationCount > 0 && (
            <Badge badgeContent={notificationCount} color="error" sx={{ mr: 1 }} />
          )}
        </Box>
        <Divider />

        {loadingNotifications ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} sx={{ color: themeColors.accent }} />
          </Box>
        ) : notifications.length > 0 ? (
          <List sx={{ py: 0 }}>
            {notifications.map((notif) => (
              <React.Fragment key={notif.id}>
                <ListItemButton
                  onClick={() => handleNotificationClick(notif.path)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': { backgroundColor: themeColors.accentHover }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <UnreadIcon sx={{ fontSize: 10, color: themeColors.accent }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={600} sx={{ color: themeColors.text }}>
                        {notif.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" sx={{ color: themeColors.textSecondary, display: 'block', mb: 0.5 }}>
                          {notif.message}
                        </Typography>
                        <Typography variant="caption" sx={{ color: themeColors.accent, fontSize: '10px', fontWeight: 500 }}>
                          {new Date(notif.date).toLocaleDateString()} at {new Date(notif.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
                <Divider component="li" />
              </React.Fragment>
            ))}
            <MenuItem
              onClick={() => {
                navigate('/inbox');
                handleNotificationClose();
              }}
              sx={{ justifyContent: 'center', py: 1, backgroundColor: 'transparent' }}
            >
              <Typography variant="caption" color="primary" fontWeight={600}>
                View All Notifications
              </Typography>
            </MenuItem>
          </List>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 40, color: themeColors.border, mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              All caught up! No pending actions.
            </Typography>
          </Box>
        )}
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={handleProfileClose}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: themeColors.surface,
              color: themeColors.text,
              borderColor: themeColors.border,
              border: `1px solid ${themeColors.border}`,
              width: { xs: '90vw', sm: '280px' },
              maxWidth: '100%',
              mt: 1.5,
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled sx={{ py: 2, opacity: '1 !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={user?.profilePicture}
              sx={{ width: 44, height: 44, border: `2px solid ${themeColors.accent}` }}
            >
              {user?.name?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: themeColors.text }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: themeColors.textSecondary }}>
                {user?.email}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            navigate('/profile');
            handleProfileClose();
          }}
          sx={{ py: 1.2 }}
        >
          <ListItemIcon>
            <ProfileIcon sx={{ color: themeColors.accent, fontSize: '20px' }} />
          </ListItemIcon>
          <Typography variant="body2">My Profile</Typography>
        </MenuItem>
        <MenuItem onClick={handleSettings} sx={{ display: { xs: 'flex', sm: 'none' }, py: 1.2 }}>
          <ListItemIcon>
            <SettingsIcon sx={{ color: themeColors.accent, fontSize: '20px' }} />
          </ListItemIcon>
          <Typography variant="body2">Settings</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ py: 1.2, color: 'error.main' }}>
          <ListItemIcon>
            <LogoutIcon sx={{ color: 'error.main', fontSize: '20px' }} />
          </ListItemIcon>
          <Typography variant="body2" fontWeight={600}>Logout</Typography>
        </MenuItem>
      </Menu>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={cancelLogout}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          pb: 1,
          fontWeight: 600,
          color: themeColors.text
        }}>
          Confirm Sign Out
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" sx={{ color: themeColors.text }}>
            Are you sure you want to sign out?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button
            onClick={cancelLogout}
            variant="outlined"
            sx={{
              borderColor: themeColors.border,
              color: themeColors.text,
              px: 3,
              borderRadius: '8px',
              '&:hover': {
                borderColor: themeColors.primary,
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmLogout}
            variant="contained"
            sx={{
              backgroundColor: themeColors.error,
              color: 'white',
              px: 3,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#d32f2f'
              }
            }}
          >
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Search Results Dropdown */}
      <Popper
        open={Boolean(searchAnchorEl)}
        anchorEl={searchAnchorEl}
        placement="bottom-start"
        transition
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper
              sx={{
                backgroundColor: themeColors.surface,
                border: `1px solid ${themeColors.border}`,
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                width: '100%',
                maxWidth: '400px',
                maxHeight: '400px',
                overflow: 'hidden',
                mt: 1
              }}
            >
              <ClickAwayListener onClickAway={handleSearchClose}>
                <Box>
                  {searchLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress size={24} sx={{ color: themeColors.accent }} />
                    </Box>
                  ) : searchResults.length > 0 ? (
                    <List sx={{ py: 0 }}>
                      {searchResults.map((result, index) => (
                        <React.Fragment key={result.id}>
                          <ListItemButton
                            onClick={() => handleSearchClick(result)}
                            sx={{
                              py: 1.5,
                              px: 2,
                              '&:hover': { backgroundColor: themeColors.accentHover },
                              borderBottom: index < searchResults.length - 1 ? `1px solid ${themeColors.border}` : 'none'
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40, color: themeColors.accent }}>
                              {result.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight={600} sx={{ color: themeColors.text }}>
                                  {result.title}
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="caption" sx={{ color: themeColors.textSecondary, display: 'block' }}>
                                    {result.description}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: themeColors.accent, fontSize: '10px', fontWeight: 500 }}>
                                    {result.category}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItemButton>
                        </React.Fragment>
                      ))}
                    </List>
                  ) : searchQuery.trim() ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <SearchIcon sx={{ fontSize: 40, color: themeColors.border, mb: 1 }} />
                      <Typography variant="body2" color="textSecondary">
                        No results found for "{searchQuery}"
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <SearchIcon sx={{ fontSize: 40, color: themeColors.border, mb: 1 }} />
                      <Typography variant="body2" color="textSecondary">
                        Start typing to search...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* Mobile Search Dialog */}
      <Dialog
        open={mobileSearchOpen}
        onClose={handleMobileSearchClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            backgroundColor: themeColors.surface
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: `1px solid ${themeColors.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SearchIcon sx={{ color: themeColors.accent }} />
            <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 600 }}>
              Search
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            size="medium"
            placeholder="Search for pages, features, or actions..."
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleSearch}
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDarkMode ? 'rgba(15, 118, 110, 0.08)' : 'rgba(15, 118, 110, 0.06)',
                '& fieldset': {
                  borderColor: themeColors.border
                },
                '&:hover fieldset': {
                  borderColor: themeColors.accent
                },
                '&.Mui-focused fieldset': {
                  borderColor: themeColors.accent,
                  borderWidth: '2px'
                }
              },
              '& .MuiInputBase-input': {
                color: themeColors.text,
                fontSize: '16px'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: themeColors.accent }} />
                </InputAdornment>
              )
            }}
          />
          
          {searchLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <CircularProgress size={24} sx={{ color: themeColors.accent }} />
            </Box>
          )}
          
          {searchResults.length > 0 && (
            <List sx={{ mt: 2, py: 0 }}>
              {searchResults.map((result, index) => (
                <React.Fragment key={result.id}>
                  <ListItemButton
                    onClick={() => {
                      handleSearchClick(result);
                      handleMobileSearchClose();
                    }}
                    sx={{
                      py: 1.5,
                      px: 1,
                      '&:hover': { backgroundColor: themeColors.accentHover },
                      borderBottom: index < searchResults.length - 1 ? `1px solid ${themeColors.border}` : 'none'
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: themeColors.accent }}>
                      {result.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight={600} sx={{ color: themeColors.text }}>
                          {result.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ color: themeColors.textSecondary, display: 'block' }}>
                            {result.description}
                          </Typography>
                          <Typography variant="caption" sx={{ color: themeColors.accent, fontSize: '11px', fontWeight: 500 }}>
                            {result.category}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          )}
          
          {!searchLoading && searchQuery.trim() && searchResults.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 3, py: 2 }}>
              <SearchIcon sx={{ fontSize: 48, color: themeColors.border, mb: 2 }} />
              <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                No results found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Try searching with different keywords
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${themeColors.border}`, px: 3, py: 2 }}>
          <Button
            onClick={handleMobileSearchClose}
            variant="outlined"
            sx={{
              borderColor: themeColors.border,
              color: themeColors.text,
              '&:hover': {
                borderColor: themeColors.accent,
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ResponsiveNavbar;
